import { fileStatus } from 'components/Recommendations/Campaign/Media/media'
import { inferPlacementType } from 'components/Recommendations/Campaign/Media/media'
import {
  validateLocationTargeting,
  isLocationTargetingEmpty,
  isGeoTargetingEmpty,
  isPlatformTargetingEmpty,
  pickGeoTargetingFields,
  GEO_TARGETING_FIELDS,
  TARGETING_PLATFORMS,
} from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'

export { isPlatformTargetingEmpty, isGeoTargetingEmpty }
import { hashObjectValues } from 'utils/helpers'
import { AssetClass, Platform } from 'api/models'
import constants from '../../../../constants'

/**
 * @typedef FBAsset
 * @property {integer} id
 * @property {string} asset_id
 * @property {string} asset_label
 * @property {string} asset_class
 * @property {string} platform
 * @property {boolean} is_active
 * @property {boolean} is_enabled
 */

export function calculateSetupCreativesFromUpdatedTCCreatives(
  creatives,
  tc,
  tc_run_id,
  tcCreatives,
) {
  const otherCreatives = creatives.filter((c) => c.tc !== tc).map((c) => ({ ...c }))

  // A youtube item has no url/width/height/status, so the field list below
  // would drop youtube_video_id and the success filter would drop the item.
  const mapSetupMediaItem = (m) => {
    if (m.youtube_video_id) {
      return { ...m }
    }

    return {
      id: m.id,
      url: m.url,
      size: m.size,
      type: m.type,
      index: m.index,
      width: m.width,
      height: m.height,
      status: m.status,
      filename: m.filename,
      sub_group_id: m.sub_group_id ?? null,
      headline: m.headline ?? null,
      ratio: m.ratio ?? null,
      placement_type: m.placement_type ?? inferPlacementType(m),
      is_generated: typeof m.is_generated === 'boolean' ? m.is_generated : false,
      generating: typeof m.generating === 'boolean' ? m.generating : false,
    }
  }

  const mapSetupMedia = (items = []) =>
    items
      .filter((x) => x.youtube_video_id || x.status === fileStatus.success)
      .map(mapSetupMediaItem)

  const media = mapSetupMedia(tcCreatives.media)
  const placement_media = mapSetupMedia(tcCreatives.placement_media)

  const newCreatives = [
    ...otherCreatives,
    { ...tcCreatives, tc, tc_run_id, media, placement_media, destination_url_inherited: false },
  ]

  if (tcCreatives.call_to_action) {
    // call_to_action is a plain string with one platform selected, or a
    // {fb, google} object with both — where an unchosen platform is ''. Merge
    // per key so a creative that inherited only one platform's CTA can still
    // inherit the other from a later edit.
    const incoming = tcCreatives.call_to_action
    const isObject = incoming && typeof incoming === 'object'

    newCreatives.forEach((c) => {
      if (!isObject) {
        if (!c.call_to_action) c.call_to_action = incoming
        return
      }

      const current = c.call_to_action && typeof c.call_to_action === 'object' ? c.call_to_action : {}
      const merged = { ...current }
      if (!merged.fb && incoming.fb) merged.fb = incoming.fb
      if (!merged.google && incoming.google) merged.google = incoming.google
      c.call_to_action = merged
    })
  }

  if (tcCreatives.destination_url) {
    const validDestinationUrls = tcCreatives.destination_url
      .filter(Boolean)
      .filter((url) => constants.URL_VALIDATION_REGEX.test(url))
    if (validDestinationUrls.length > 0) {
      newCreatives.forEach((c) => {
        if (
          c.tc !== tc &&
          (c.destination_url_inherited === true ||
            typeof c.destination_url_inherited === 'undefined')
        ) {
          c.destination_url = [...validDestinationUrls]
          c.destination_url_inherited = true
        }
      })
    }
  }

  return newCreatives
}
export function getDisabledVariationHashes(setup) {
  if (!setup) {
    return {}
  }

  const { creatives } = setup
  const integrityHashes = creatives.reduce((acc, c) => {
    acc[c.tc] = []
    return acc
  }, {})

  const disabledVariations = creatives.reduce((acc, c) => {
    acc[c.tc] = c.excluded_variations
    return acc
  }, {})

  try {
    for (const tc of Object.keys(disabledVariations)) {
      const tcCreative = creatives.find((c) => c.tc === tc)
      for (const variation of disabledVariations[tc] || []) {
        const [headlineIndex, bodyIndex, mediaIndex, destinationUrlIndex] = variation
          .split('')
          .map(Number)
        const headline = tcCreative?.headline?.[headlineIndex - 1]
        const body = tcCreative?.body?.[bodyIndex - 1]
        const mediaUrl =
          tcCreative?.media?.[mediaIndex - 1]?.url ||
          tcCreative?.carousel_cards?.[mediaIndex - 1]?.media?.url
        const destinationUrl = tcCreative?.destination_url?.[destinationUrlIndex - 1]

        const hash = hashObjectValues({
          headline,
          body,
          media: mediaUrl,
          destinationUrl,
        })

        integrityHashes[tcCreative.tc].push({
          [variation]: hash,
        })
      }
    }
  } catch (error) {
    console.error('Failed to init disabled variations cache', error)
  }

  return integrityHashes
}
export function _checkDisabledVariationsIntegrity(
  creatives,
  integrityHashes,
  currentDisabledVariations,
) {
  const disabledVariations = creatives.reduce((acc, creative) => {
    acc[creative.tc] = creative.excluded_variations
    return acc
  }, {})

  for (const tc of Object.keys(disabledVariations)) {
    const tcCreative = [creatives].flat().find((c) => c.tc === tc)
    for (const variation of disabledVariations[tc] || []) {
      if (currentDisabledVariations && !currentDisabledVariations[tc]?.includes(variation)) {
        // Disabled variation was removed
        continue
      }
      const [headlineIndex, bodyIndex, mediaIndex, destinationUrlIndex] = variation
        .split('')
        .map(Number)
      const headline = tcCreative?.headline?.[headlineIndex - 1]
      const body = tcCreative?.body?.[bodyIndex - 1]
      const mediaUrl =
        tcCreative?.media?.[mediaIndex - 1]?.url ||
        tcCreative?.carousel_cards?.[mediaIndex - 1]?.media?.url
      const destinationUrl = tcCreative?.destination_url?.[destinationUrlIndex - 1]

      const hash = hashObjectValues({
        headline,
        body,
        media: mediaUrl,
        destinationUrl,
      })

      const originalHash = integrityHashes[tc]?.find((x) => x[variation])

      // No original hash means this disabled variation is new.
      if (originalHash && hash !== originalHash[variation]) {
        return false
      }
    }
  }

  return true
}
/**
 * Builds the platform-nested targeting payload from one updated TC's
 * targeting, seeding every TC that has none or has invalid targeting for any
 * of `platforms` with the source TC's geo.
 *
 * @param {Object} tcTargeting source targeting, `{tc, tc_run_id, fb?, google?}`
 * @param {string[]} platforms active platform keys, from activeTargetingPlatforms()
 */
export function buildTargetingPayload(setup, tcTargeting, platforms) {
  const tcs = setup.creatives.map((x) => ({ tc: x.tc, tc_run_id: x.tc_run_id }))
  const { targeting } = setup
  const resultPayload = []
  const _hasAudienceTargeting = hasAudienceTargeting(tcTargeting)

  for (const tasteCluster of tcs) {
    if (tasteCluster.tc === tcTargeting.tc) {
      resultPayload.push(tcTargeting)
      continue
    }

    const currentTcTargeting = targeting.find((y) => y.tc === tasteCluster.tc)
    const currentIsValid =
      currentTcTargeting &&
      validateLocationTargeting(
        targeting,
        tasteCluster.tc,
        tasteCluster.tc_run_id,
        _hasAudienceTargeting,
        hasSavedAudienceTargeting(currentTcTargeting),
        platforms,
        setup,
      ).valid &&
      !platforms.some((platform) => isLocationTargetingEmpty(currentTcTargeting, platform))

    if (!currentIsValid) {
      // Only geo propagates, merged onto the target's own entry: audience and
      // age_range are per-TC and must never be sourced from another TC.
      const tcPayload = {
        ...currentTcTargeting,
        tc: tasteCluster.tc,
        tc_run_id: tasteCluster.tc_run_id,
      }
      for (const platform of platforms) {
        const geo = pickGeoTargetingFields(tcTargeting[platform])
        if (Object.keys(geo).length) {
          tcPayload[platform] = { ...currentTcTargeting?.[platform], ...geo }
        }
      }
      resultPayload.push(tcPayload)
    } else {
      resultPayload.push(currentTcTargeting)
    }
  }

  return resultPayload
}

/**
 * Moves a gated platform's geo out of the per-TC entries and into one
 * campaign-wide value: only TC #1 contributes it, every TC gets it stripped.
 * Audience and age_range are never touched. See README.md.
 *
 * @param {boolean} isFirstTc caller-supplied, since it can't be derived from
 *   targeting[0]; when false the strip still happens but nothing is extracted
 * @returns {{targeting: Object[], campaignTargeting: Object|null}}
 */
export function extractCampaignWideTargeting(targeting, gatedPlatforms, isFirstTc, tc) {
  if (!gatedPlatforms.length) return { targeting, campaignTargeting: null }

  const sourceTc = isFirstTc ? targeting.find((x) => x.tc === tc) : null
  const campaignTargeting = {}
  if (sourceTc) {
    for (const platform of gatedPlatforms) {
      // Keyed on targeting_mode presence, not content — a mode switch with
      // nothing added yet must still overwrite stale campaign_targeting.
      const mode = sourceTc[platform]?.targeting_mode
      if (mode) {
        campaignTargeting[platform] = pickGeoTargetingFields(sourceTc[platform])
      }
    }
  }

  const strippedTargeting = targeting.map((tcTargeting) => {
    const result = { ...tcTargeting }
    for (const platform of gatedPlatforms) {
      if (!result[platform]) continue
      const rest = { ...result[platform] }
      for (const field of GEO_TARGETING_FIELDS) delete rest[field]
      if (isPlatformTargetingEmpty(rest)) {
        delete result[platform]
      } else {
        result[platform] = rest
      }
    }
    return result
  })

  return {
    targeting: strippedTargeting,
    campaignTargeting: Object.keys(campaignTargeting).length ? campaignTargeting : null,
  }
}

// True if this TC has an include-audience selection on any active platform.
// Selecting one bypasses geo-targeting *propagation*, and removes fd_targeting
// from the mode options — but NOT geo validation: a custom audience is a plain
// criterion list, so explicit locations are still required (only Meta's
// saved_audience_id, a full targeting spec, waives them — see
// hasSavedAudienceTargeting below).
export function hasAudienceTargeting(tcTargeting) {
  if (!tcTargeting) return false
  return !!(
    tcTargeting.fb?.custom_audiences?.length ||
    tcTargeting.google?.audiences?.length ||
    tcTargeting.google?.audience_resource_names?.length
  )
}

// Meta's saved_audience_id is a full targeting-spec merge that overrides geo
// entirely. FB-only: Google's audience_resource_names is a plain criterion
// list, so it doesn't bypass geo the same way.
export function hasSavedAudienceTargeting(tcTargeting) {
  return !!tcTargeting?.fb?.saved_audience_id
}

// The shared location controls can leave a newly-added platform with only a
// mode and empty arrays, a shape the backend schema rejects. Applied before
// every targeting payload send in asyncHandlers.js. See README.md.
export function stripEmptyPlatformTargeting(targeting = []) {
  return targeting.map((tcTargeting) => {
    const stripped = { ...tcTargeting }
    for (const platform of Object.values(TARGETING_PLATFORMS)) {
      if (platform in stripped && isPlatformTargetingEmpty(stripped[platform])) {
        delete stripped[platform]
      }
    }
    return stripped
  })
}

// One control fans the same value out to every active platform, so the first
// platform sub-object that has an age_range set reflects the saved value.
export function getAgeTargetingFromSetup(setup, platforms) {
  const defaultAgeRange = { min: 18, max: 65 }
  if (!setup || !Array.isArray(setup.targeting)) {
    return defaultAgeRange
  }
  for (const tcTargeting of setup.targeting) {
    for (const platform of platforms) {
      const ageRange = tcTargeting[platform]?.age_range
      if (ageRange && ageRange.min !== undefined && ageRange.max !== undefined) {
        return { min: ageRange.min, max: ageRange.max }
      }
    }
  }
  return defaultAgeRange
}

// Writes into each active platform's own age_range sub-object on every TC —
// never a top-level key, which the backend schema rejects.
export function applyAgeTargetingToTargeting(targeting, platforms, min, max) {
  return targeting.map((tcTargeting) => {
    const updated = { ...tcTargeting }
    platforms.forEach((platform) => {
      const existing = tcTargeting[platform]?.age_range
      updated[platform] = {
        ...tcTargeting[platform],
        age_range: {
          min: min !== undefined ? min : existing?.min || 18,
          max: max !== undefined ? max : existing?.max || 65,
        },
      }
    })
    return updated
  })
}

/**
 * Facebook profiles selectable for `adAccount`. Falls back to every enabled
 * profile while Meta is disconnected, since connections can't be read yet.
 *
 * @param {Array<FBAsset>} accounts
 * @param {FBAsset} adAccount
 * @param {boolean} metaConnectionStatus
 */
export function getAvailablePages(accounts, adAccount, metaConnectionStatus) {
  if (!metaConnectionStatus) {
    return accounts.filter(
      (x) =>
        x.asset_class === AssetClass.profile && x.platform === Platform.facebook && x.is_enabled,
    )
  }

  if (!adAccount) {
    return []
  }

  return accounts.filter(
    (x) =>
      x.asset_class === AssetClass.profile &&
      x.platform === Platform.facebook &&
      x.is_enabled &&
      x.connected_assets.some(
        (y) =>
          y.asset_id === adAccount.asset_id &&
          y.asset_class === AssetClass.ad_account &&
          y.platform === Platform.facebook &&
          y.is_active,
      ),
  )
}

/**
 * Pixels selectable for `adAccount`. Same disconnected fallback as
 * getAvailablePages.
 *
 * @param {Array<FBAsset>} accounts
 * @param {FBAsset} adAccount
 * @param {boolean} metaConnectionStatus
 */
export function getAvailablePixels(accounts, adAccount, metaConnectionStatus) {
  if (!metaConnectionStatus) {
    return accounts.filter(
      (x) => x.asset_class === AssetClass.pixel && x.platform === Platform.facebook && x.is_enabled,
    )
  }

  if (!adAccount) {
    return []
  }

  return accounts.filter(
    (x) =>
      x.asset_class === AssetClass.pixel &&
      x.platform === Platform.facebook &&
      x.is_enabled &&
      x.connected_assets.some(
        (y) =>
          y.asset_id === adAccount.asset_id &&
          y.asset_class === AssetClass.ad_account &&
          y.platform === Platform.facebook &&
          y.is_active,
      ),
  )
}

/**
 * Instagram profiles connected to either `adAccount` or `page` — either one
 * alone is enough. Same disconnected fallback as getAvailablePages.
 *
 * @param {Array<FBAsset>} accounts
 * @param {FBAsset} adAccount
 * @param {FBAsset} page selected Facebook profile
 * @param {boolean} metaConnectionStatus
 */
export function getAvailableInstagramAccounts(accounts, adAccount, page, metaConnectionStatus) {
  if (!metaConnectionStatus) {
    return accounts.filter(
      (x) =>
        x.asset_class === AssetClass.profile && x.platform === Platform.instagram && x.is_enabled,
    )
  }

  if (!page && !adAccount) {
    return []
  }

  return accounts.filter(
    (x) =>
      x.asset_class === AssetClass.profile &&
      x.platform === Platform.instagram &&
      x.is_enabled &&
      ((adAccount &&
        x.connected_assets.some(
          (y) =>
            y.asset_id === adAccount.asset_id &&
            y.asset_class === AssetClass.ad_account &&
            y.platform === Platform.facebook &&
            y.is_active,
        )) ||
        (page &&
          x.connected_assets.some(
            (y) =>
              y.asset_id === page.asset_id &&
              y.asset_class === AssetClass.profile &&
              y.platform === Platform.facebook &&
              y.is_active,
          ))),
  )
}
